/**
 * SlackInboundSettings Component
 *
 * Connect Slack as an INBOUND channel (Epic AC-1819 / AC-1823): converse with the
 * Agent Commander assistant directly from Slack. Two steps:
 *   1. Connect the Slack app -- store the workspace ID + Signing Secret (from the
 *      app's Basic Information page) so POST /slack/events can verify requests.
 *   2. Link your Slack identity -- generate a one-time code, then DM the bot
 *      `link <code>` (prove-by-messaging).
 *
 * Mirrors WhatsAppSettings (the inbound WhatsApp assistant): the assistant brain
 * is shared across channels; this panel only links a Slack identity and stores
 * the per-workspace signing secret.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Slack, Wifi, WifiOff, Loader, Check, AlertCircle, Copy, Link2 } from 'lucide-react';
import apiService from '../../services/api';

/** Extract a user-friendly error message from an Axios error or generic error. */
function getErrorMessage(err: any, fallback: string): string {
  return err?.response?.data?.detail || err?.message || fallback;
}

const INPUT_CLASS =
  'w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md ' +
  'bg-white dark:bg-gray-800 text-gray-900 dark:text-warm-white ' +
  'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 dark:focus:ring-offset-cap-dark';

const BTN_FOCUS =
  'focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1 dark:focus:ring-offset-cap-dark';

const SlackInboundSettings: React.FC = () => {
  const [linked, setLinked] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [teamId, setTeamId] = useState('');
  const [signingSecret, setSigningSecret] = useState('');
  const [linkCode, setLinkCode] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Guard async setState against teardown (a tab switch can unmount mid-request).
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Auto-dismiss transient success banners (mirrors WhatsAppSettings).
  useEffect(() => {
    if (!success) return undefined;
    const t = setTimeout(() => {
      if (isMountedRef.current) setSuccess(null);
    }, 5000);
    return () => clearTimeout(t);
  }, [success]);

  const loadStatus = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const [cfg, status] = await Promise.all([
        apiService.slackGetConfig(),
        apiService.slackLinkStatus(),
      ]);
      if (!isMountedRef.current) return;
      setConfigured(cfg.configured);
      if (cfg.team_id) setTeamId(cfg.team_id);
      setLinked(status.linked);
    } catch (err) {
      if (isMountedRef.current) setError(getErrorMessage(err, 'Failed to load Slack status'));
    } finally {
      if (isMountedRef.current) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleSaveConfig = async () => {
    if (!teamId.trim() || !signingSecret.trim()) {
      setSuccess(null);
      setError('Workspace ID and Signing Secret are both required.');
      return;
    }
    setIsSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await apiService.slackSaveConfig(teamId.trim(), signingSecret.trim());
      if (!isMountedRef.current) return;
      setConfigured(res.configured);
      setSigningSecret('');
      setSuccess('Signing secret saved. Agent Commander can now verify inbound Slack events.');
    } catch (err) {
      if (isMountedRef.current) setError(getErrorMessage(err, 'Failed to save Slack configuration'));
    } finally {
      if (isMountedRef.current) setIsSaving(false);
    }
  };

  const handleGenerateCode = async () => {
    setIsGenerating(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await apiService.slackLinkStart();
      if (!isMountedRef.current) return;
      setLinkCode(res.code);
      setSuccess('Link code generated. DM it to your bot to finish linking.');
    } catch (err) {
      if (isMountedRef.current) setError(getErrorMessage(err, 'Failed to generate link code'));
    } finally {
      if (isMountedRef.current) setIsGenerating(false);
    }
  };

  const handleCopy = (text: string) => {
    setError(null);
    if (!navigator.clipboard) {
      setError('Could not copy automatically — select and copy the command manually.');
      return;
    }
    navigator.clipboard.writeText(text).then(
      () => setSuccess('Copied to clipboard'),
      () => setError('Could not copy — select and copy the command manually.'),
    );
  };

  const configIncomplete = !teamId.trim() || !signingSecret.trim();

  return (
    <div
      className="bg-white dark:bg-cap-dark rounded-lg shadow p-4 sm:p-6"
      data-testid="slack-inbound-settings"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Slack className="w-5 h-5 text-gray-600 dark:text-gray-400 mr-2" aria-hidden="true" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white">
            Slack Assistant (Inbound)
          </h2>
        </div>
        {linked ? (
          <span className="flex items-center text-sm text-green-600 dark:text-green-400">
            <Wifi className="w-4 h-4 mr-1" aria-hidden="true" /> Linked
          </span>
        ) : (
          <span className="flex items-center text-sm text-gray-500 dark:text-gray-400">
            <WifiOff className="w-4 h-4 mr-1" aria-hidden="true" /> Not linked
          </span>
        )}
      </div>

      <p className="text-gray-500 dark:text-gray-400 text-sm mb-4">
        Chat with the Agent Commander assistant directly from Slack &mdash; DM the bot
        or @-mention it to run, list, or build workflows.
      </p>

      {error && (
        <div className="mb-3 flex items-start text-sm text-red-600 dark:text-red-400" role="alert">
          <AlertCircle className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="mb-3 flex items-start text-sm text-green-600 dark:text-green-400" role="status">
          <Check className="w-4 h-4 mr-1 mt-0.5 flex-shrink-0" aria-hidden="true" />
          <span>{success}</span>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center text-gray-500 dark:text-gray-400 text-sm">
          <Loader className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" /> Loading&hellip;
        </div>
      ) : (
        <div className="space-y-6">
          {/* Step 1: connect the Slack app (signing secret) */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              1. Connect your Slack app
              {configured && (
                <>
                  <Check className="ml-1 w-4 h-4 text-green-600 dark:text-green-400" aria-hidden="true" />
                  <span className="sr-only">completed</span>
                </>
              )}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              From your Slack app&rsquo;s Basic Information page, copy the Workspace ID and
              Signing Secret, then point Event Subscriptions at{' '}
              <code className="font-mono">/api/v1/slack/events</code>.
            </p>
            <div className="space-y-2">
              <div>
                <label
                  htmlFor="slack-team-id"
                  className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Workspace ID
                </label>
                <input
                  id="slack-team-id"
                  type="text"
                  value={teamId}
                  onChange={(e) => setTeamId(e.target.value)}
                  placeholder="e.g. T01ABC234"
                  className={INPUT_CLASS}
                />
              </div>
              <div>
                <label
                  htmlFor="slack-signing-secret"
                  className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1"
                >
                  Signing Secret
                </label>
                <input
                  id="slack-signing-secret"
                  type="password"
                  value={signingSecret}
                  onChange={(e) => setSigningSecret(e.target.value)}
                  placeholder="Signing Secret"
                  className={INPUT_CLASS}
                />
              </div>
              <button
                type="button"
                onClick={handleSaveConfig}
                disabled={isSaving || configIncomplete}
                className={`inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 ${BTN_FOCUS} disabled:opacity-50 disabled:cursor-not-allowed transition-colors`}
              >
                {isSaving && <Loader className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />}
                {configured ? 'Update signing secret' : 'Save signing secret'}
              </button>
            </div>
          </div>

          {/* Step 2: link the Slack identity (prove-by-messaging) */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              2. Link your Slack account
              {linked && (
                <>
                  <Check className="ml-1 w-4 h-4 text-green-600 dark:text-green-400" aria-hidden="true" />
                  <span className="sr-only">completed</span>
                </>
              )}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Generate a one-time code, then DM your Agent Commander bot in Slack with{' '}
              <code className="font-mono">link &lt;code&gt;</code>.
            </p>
            {linkCode ? (
              <div className="flex items-center gap-2">
                <code className="px-3 py-2 text-sm bg-gray-100 dark:bg-gray-800 rounded-md font-mono text-gray-900 dark:text-warm-white">
                  link {linkCode}
                </code>
                <button
                  type="button"
                  onClick={() => handleCopy(`link ${linkCode}`)}
                  aria-label="Copy link command"
                  className={`p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 rounded ${BTN_FOCUS}`}
                >
                  <Copy className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={handleGenerateCode}
                disabled={isGenerating}
                className={`inline-flex items-center px-4 py-2 border border-indigo-600 text-indigo-600 dark:text-indigo-400 text-sm font-medium rounded-lg hover:bg-indigo-50 dark:hover:bg-indigo-900/20 ${BTN_FOCUS} disabled:opacity-50 transition-colors`}
              >
                {isGenerating ? (
                  <Loader className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                ) : (
                  <Link2 className="w-4 h-4 mr-2" aria-hidden="true" />
                )}
                Generate link code
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SlackInboundSettings;
